import { ActivitySegment, ActivityType, StatusTone } from 'app/data/types/activity';

/**
 * Status configuration — the closed FE union that maps a canonical `statusKey` to a pill tone and a
 * label translation id. Per the data contract, the label is resolved uniformly as
 * `details.statusLabel ?? localize(statusKey)`; only work-order (tenant-customizable) rows carry a
 * BE `statusLabel`. An unknown key falls back to `neutral` tone + a humanized label so a
 * newly-derived key degrades gracefully instead of breaking the render.
 */

/**
 * Semantic pill tone → dot color, sourced from DS tokens (federation-safe `var(--token, #fallback)`)
 * so they track the design system, not a private copy. Independent of the tenant brand accent.
 *
 * `warning` (#ff9901 amber) has no DS equivalent — the mock's pending/expected accent. Kept as a
 * literal pending a design/DS decision (add a token, or collapse to `--color-system-orange`).
 */
export const TONE_DOT: Record<StatusTone, string> = {
  neutral: 'var(--color-system-grey-medium-alt, #939ba2)',
  info: 'var(--color-system-blue, #044aef)',
  warning: '#ff9901',
  progress: 'var(--color-system-orange, #cd3d00)',
  success: 'var(--color-system-green, #09794e)',
  critical: 'var(--color-system-pink, #d52465)',
};

/**
 * The work-orders status palette, as configured per building. Ten named tokens rather than hex, so a
 * building picks "purple" and the product decides what purple is.
 *
 * Resolved to Tailwind's default scale at the 400 shade, which is what work-orders-admin paints its own
 * status swatches with (`STATUS_COLOR_MAP`, `bg-{color}-400`). Matching it means the dot a tenant sees
 * here is the colour the admin picked in settings, not an approximation of it.
 *
 * Each carries the literal too. The build does emit all ten vars today — verified against the production
 * bundle, and no `bg-*-400` utility exists anywhere to explain it — but Tailwind prunes unreferenced theme
 * variables in principle, and these are only ever referenced from an inline style, which is not usage it
 * can see. A dot that silently computes to `transparent` is not a failure worth risking for the brevity.
 */
export const STATUS_COLOR_DOT: Record<string, string> = {
  blue: 'var(--color-blue-400, #54a2ff)',
  indigo: 'var(--color-indigo-400, #7d87ff)',
  green: 'var(--color-green-400, #05df72)',
  teal: 'var(--color-teal-400, #00d3bd)',
  yellow: 'var(--color-yellow-400, #fac800)',
  orange: 'var(--color-orange-400, #ff8b1a)',
  red: 'var(--color-red-400, #ff6568)',
  purple: 'var(--color-purple-400, #c07eff)',
  pink: 'var(--color-pink-400, #fb64b6)',
  gray: 'var(--color-gray-400, #99a1af)',
};

interface StatusMeta {
  tone: StatusTone;
  /** react-intl id under `activity.status.*` (fixed enums only; WO uses `statusLabel`). */
  labelId: string;
}

/** Canonical fixed key sets per type (data-contract §1) + common work-order tokens. */
export const STATUS_META: Record<string, StatusMeta> = {
  // resource_booking
  confirmed: { tone: 'success', labelId: 'activity.status.confirmed' },
  requested: { tone: 'warning', labelId: 'activity.status.requested' },
  pending_approval: { tone: 'info', labelId: 'activity.status.pendingApproval' },
  // visitor_registration
  expected: { tone: 'warning', labelId: 'activity.status.expected' },
  // A watchlist hold is not ordinary approval-pending — the visitor has been flagged, so it reads as
  // needing attention rather than merely queued.
  pending_watchlist: { tone: 'progress', labelId: 'activity.status.pendingWatchlist' },
  checked_in: { tone: 'success', labelId: 'activity.status.checkedIn' },
  checked_out: { tone: 'neutral', labelId: 'activity.status.checkedOut' },
  // A refusal, not a cancellation: critical rather than neutral, and it stays in Upcoming until the
  // visit date passes so the host can see it happened.
  denied: { tone: 'critical', labelId: 'activity.status.denied' },
  // event
  registered: { tone: 'info', labelId: 'activity.status.registered' },
  // Not registered: a seat isn't held yet, so it reads as pending rather than confirmed. Covers
  // WAITING_FOR_CONFIRMATION too — the BE projects both waitlist states to one key.
  waitlisted: { tone: 'warning', labelId: 'activity.status.waitlisted' },
  attended: { tone: 'success', labelId: 'activity.status.attended' },
  // work_order (system tokens; admin-renamed rows arrive with a BE statusLabel)
  in_progress: { tone: 'progress', labelId: 'activity.status.inProgress' },
  quote_pending: { tone: 'progress', labelId: 'activity.status.quotePending' },
  resolved: { tone: 'success', labelId: 'activity.status.resolved' },
  // shared terminals
  completed: { tone: 'success', labelId: 'activity.status.completed' },
  cancelled: { tone: 'critical', labelId: 'activity.status.cancelled' },
  // past-but-unresolved (a past invite never checked in, a past unconfirmed event) — neutral, not a
  // positive "attended"/"checked in".
  expired: { tone: 'neutral', labelId: 'activity.status.expired' },
  // Booked and not turned up to. Neutral like `expired` rather than critical like `denied`: it is a
  // record of what happened, not a refusal, and nothing about it is the reader's to act on now.
  // Distinct from `cancelled` — giving a slot back and simply not using it read differently to the
  // person who booked it, and to whoever pays a late fee for one of them.
  no_show: { tone: 'neutral', labelId: 'activity.status.noShow' },
};

/**
 * The visitor-registration palette, matched to `visit-status-colors.ts` in visitor-registration-admin
 * (`statusColorsMap`), which the tenant app's own badge mirrors.
 *
 * A visit is shown in two places — the visits list and this feed — and reading grey for cancelled in one
 * and pink in the other makes them look like different states. The tones here are semantic and shared
 * across every type, so `cancelled` cannot simply be recoloured: it would drag bookings, events and work
 * orders with it. Visits get their own map instead.
 *
 * Literals where the DS token would be the wrong colour rather than merely absent: this app's
 * `--color-system-green` is #09794e, while visitor-registration paints checked-in with systemGreenAlt,
 * and its greys differ too. Pointing at the token would quietly reintroduce the mismatch.
 */
export const VISIT_STATUS_DOT: Record<string, string> = {
  expected: '#991BBA',
  // A visit awaiting approval is orange in the admin; `pending_approval` reads blue here because that
  // tone was set for a resource booking, where it means a request is queued rather than a person is.
  pending_approval: 'var(--color-system-orange, #cd3d00)',
  // Pink, not orange: the admin colours a watchlist hold as a refusal risk rather than a queue.
  pending_watchlist: 'var(--color-system-pink, #d52465)',
  checked_in: '#0CA883',
  checked_out: 'var(--color-system-orange, #cd3d00)',
  denied: 'var(--color-system-pink, #d52465)',
  cancelled: '#878E97',
  expired: '#878E97',
};

/**
 * The resource-booking palette, matched to `bookingsTable.constants.ts` in hqos — the requests table a
 * building manager approves and denies bookings in.
 *
 * Same reasoning as visits: a booking is shown to the tenant here and to the building there, and the
 * two reading different colours for one state makes them look like different states. Only three keys
 * differ from the semantic tone; `confirmed` and `denied` already land on admin's exact hexes
 * (`#09794e`, `#d52465`), which is why they are absent rather than restated.
 *
 * hqos resolves its `variant` through `@hqo/react-components-library`'s StatusIndicator —
 * `success: admin.green`, `warning: admin.orange`, `dark: greys.adminGreyDark`, `danger: admin.pink` —
 * except No Show and Late Cancel, which carry an explicit `PURPLE_STATUS_COLOR`. Literals here rather
 * than this app's DS tokens: admin's greys and oranges are its own, so pointing at a token would
 * quietly reintroduce the mismatch.
 */
export const RESOURCE_BOOKING_STATUS_DOT: Record<string, string> = {
  // hqos `dark` (greys.adminGreyDark). Grey, not the shared `critical` pink: giving a slot back is a
  // record of what happened, not a refusal — only `denied` is a refusal.
  cancelled: '#696e72',
  // hqos `warning` (admin.orange). The semantic tone reads blue here, which was a guess about a queued
  // request; the requests table this mirrors has always painted a pending booking orange.
  pending_approval: '#d55021',
  // hqos `PURPLE_STATUS_COLOR`, shared with Late Cancel — admin gives both their own colour rather
  // than folding them into cancelled.
  no_show: '#991bba',
};

/** Fallback for a key the FE has no mapping for. */
export const DEFAULT_STATUS_META: StatusMeta = { tone: 'neutral', labelId: '' };

export const getStatusMeta = (statusKey: string): StatusMeta =>
  STATUS_META[statusKey] ?? DEFAULT_STATUS_META;

/** Turn an unknown snake_case key into a human label ("pending_review" → "Pending review"). */
export const humanizeStatusKey = (statusKey: string): string => {
  const spaced = statusKey.replace(/[_-]+/g, ' ').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : '';
};

/** Terminal/past status keys — an item with one of these belongs to the Completed pane. */
export const COMPLETED_STATUS_KEYS = new Set([
  'completed',
  'attended',
  'checked_in',
  'resolved',
  'cancelled',
  // Terminal by definition: the slot has been and gone. Nothing follows a no-show.
  'no_show',
]);

export const deriveSegment = (statusKey: string): ActivitySegment => (
  COMPLETED_STATUS_KEYS.has(statusKey) ? 'completed' : 'upcoming'
);

/** The per-type palettes, for the types whose admin disagrees with the shared semantic tone. */
const TYPE_STATUS_DOT: Partial<Record<ActivityType, Record<string, string>>> = {
  visitor_registration: VISIT_STATUS_DOT,
  resource_booking: RESOURCE_BOOKING_STATUS_DOT,
};

/**
 * The dot colour for a status, in precedence order: the building's own configured palette token, then
 * the per-type palette, then the semantic tone.
 *
 * The configured token stays first — a work order's colour is chosen by the building in work-orders
 * admin, and nothing here should override what a tenant picked. An unrecognised token falls back rather
 * than reaching the style attribute unvalidated.
 */
export const resolveStatusDotColor = (
  statusKey: string,
  statusColor?: string,
  type?: ActivityType,
): string => (statusColor && STATUS_COLOR_DOT[statusColor])
  || (type ? TYPE_STATUS_DOT[type]?.[statusKey] : undefined)
  || TONE_DOT[getStatusMeta(statusKey).tone];
